import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';

interface GhlLocation {
  locationId: string;
  locationName: string;
  status: 'pending' | 'onboarded' | 'muted';
  firstSeenAt: string;
  lastSeenAt: string;
  callsSeen: number;
  callsIgnored: number;
  failedCalls: number;
  sampleContacts: string[];
  hasPit: boolean;
  mutedAt: string;
  onboardedAt: string;
}

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  pending: { label: 'Needs PIT', cls: 'badge badge-warning' },
  onboarded: { label: 'Onboarded', cls: 'badge badge-success' },
  muted: { label: 'Muted', cls: 'badge' },
};

function fmtDate(iso: string): string {
  return iso ? new Date(iso).toLocaleDateString() : '-';
}

export default function GhlLocations() {
  const [locations, setLocations] = useState<GhlLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  // Onboard modal state
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [onboardTarget, setOnboardTarget] = useState<GhlLocation | null>(null);
  const [pitInput, setPitInput] = useState('');
  const [locIdInput, setLocIdInput] = useState('');
  const [onboardError, setOnboardError] = useState('');
  const [onboardBusy, setOnboardBusy] = useState(false);
  const [onboardResult, setOnboardResult] = useState<{ locationName: string; recoveredCount: number } | null>(null);

  const loadData = () => {
    setLoading(true);
    api.getGhlLocations()
      .then((data: any) => { setLocations(data.locations || []); setLoadError(false); })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };
  useEffect(loadData, []);

  const doMute = async (loc: GhlLocation) => {
    const name = loc.locationName || loc.locationId;
    if (!window.confirm(`Mute ${name}? Future calls from this location will be dropped (not stored), and its queued failures leave Quarantine. You can unmute any time.`)) return;
    setBusyId(loc.locationId);
    try { await api.ghlMute(loc.locationId); loadData(); }
    catch { alert('Mute failed — try again'); }
    finally { setBusyId(null); }
  };

  const doRename = async (loc: GhlLocation) => {
    const input = window.prompt(
      'Account name (also updates call attribution for future recordings — past recordings keep their original name):',
      loc.locationName || '');
    if (input === null) return;
    const name = input.trim();
    if (!name) return;
    setBusyId(loc.locationId);
    try { await api.ghlRename(loc.locationId, name); loadData(); }
    catch (e: any) { alert(e?.message || 'Rename failed — try again'); }
    finally { setBusyId(null); }
  };

  const doUnmute = async (loc: GhlLocation) => {
    setBusyId(loc.locationId);
    try { await api.ghlUnmute(loc.locationId); loadData(); }
    catch { alert('Unmute failed — try again'); }
    finally { setBusyId(null); }
  };

  const openOnboard = (loc: GhlLocation | null) => {
    setOnboardTarget(loc);
    setLocIdInput(loc?.locationId || '');
    setPitInput('');
    setOnboardError('');
    setOnboardResult(null);
    setOnboardOpen(true);
  };

  const doOnboard = async () => {
    if (!locIdInput.trim() || !pitInput.trim()) { setOnboardError('Location ID and PIT are both required'); return; }
    setOnboardBusy(true);
    setOnboardError('');
    try {
      const result = await api.ghlOnboard(locIdInput.trim(), pitInput.trim());
      setOnboardResult({ locationName: result.locationName || locIdInput.trim(), recoveredCount: result.recoveredCount || 0 });
      setPitInput('');
      loadData();
    } catch (e: any) {
      setOnboardError(e?.message || 'Validation failed — check the PIT scopes and location ID');
    } finally {
      setOnboardBusy(false);
    }
  };

  // Client-side pagination — the API returns the full sorted list (tens of
  // rows); safePage clamps when a reload shrinks the list below the current page.
  const PAGE_SIZE = 25;
  const totalPages = Math.max(1, Math.ceil(locations.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages - 1);
  const pageLocations = locations.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  return (
    <div>
      <div className="page-header">
        <h2>GHL Locations</h2>
        <p style={{ color: '#888', marginTop: 4 }}>
          Every LeadMomentum location that has sent call webhooks. Onboard a location with its
          Private Integration Token (PIT) to start archiving its recordings, or mute it to ignore
          its calls entirely. Per-call failures live in <Link to="/quarantine">Quarantine → Missing PIT</Link>.
        </p>
        <button onClick={() => openOnboard(null)} style={{ marginTop: 8 }}>+ Add PIT manually</button>
      </div>

      {loadError && (
        <div className="setup-card" style={{ borderLeft: '4px solid #e53935', marginBottom: 12 }}>
          Failed to load locations. <button onClick={loadData}>Retry</button>
        </div>
      )}
      {loading ? <p>Loading…</p> : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Location</th>
              <th>Status</th>
              <th>First seen</th>
              <th>Last seen</th>
              <th>Seen</th>
              <th>Failed</th>
              <th>Ignored</th>
              <th>Recent contacts</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageLocations.map(loc => {
              const badge = STATUS_BADGE[loc.status] || STATUS_BADGE.pending;
              const busy = busyId === loc.locationId;
              return (
                <tr key={loc.locationId}>
                  <td>
                    <div>
                      {loc.locationName || <em>Unnamed</em>}
                      <button disabled={busy} onClick={() => doRename(loc)} title="Rename account (reused accounts)"
                        style={{ marginLeft: 8, fontSize: 11, padding: '1px 8px' }}>Edit</button>
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#888' }}>{loc.locationId}</div>
                  </td>
                  <td><span className={badge.cls}>{badge.label}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(loc.firstSeenAt)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(loc.lastSeenAt)}</td>
                  <td>{loc.callsSeen}</td>
                  <td>{loc.failedCalls > 0 ? <span className="badge badge-warning">{loc.failedCalls}</span> : 0}</td>
                  <td>{loc.callsIgnored}</td>
                  <td style={{ fontSize: 12, maxWidth: 220 }}>{loc.sampleContacts.join(', ') || '-'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {loc.status !== 'onboarded' && loc.status !== 'muted' && (
                      <button disabled={busy} onClick={() => openOnboard(loc)}>Add PIT</button>
                    )}
                    {loc.status === 'onboarded' && (
                      <button disabled={busy} onClick={() => openOnboard(loc)}>Replace PIT</button>
                    )}
                    {loc.status === 'muted'
                      ? <button disabled={busy} onClick={() => doUnmute(loc)} style={{ marginLeft: 6 }}>Unmute</button>
                      : <button disabled={busy} onClick={() => doMute(loc)} style={{ marginLeft: 6 }}>Mute</button>}
                  </td>
                </tr>
              );
            })}
            {locations.length === 0 && (
              <tr><td colSpan={9} style={{ textAlign: 'center', color: '#888' }}>No GHL locations seen yet</td></tr>
            )}
          </tbody>
        </table>
      )}

      {!loading && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16, fontSize: 14 }}>
          <button onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={safePage === 0}
            style={{ padding: '6px 14px', border: '1px solid #ddd', borderRadius: 6, cursor: safePage === 0 ? 'default' : 'pointer', background: safePage === 0 ? '#f5f5f5' : '#fff' }}>
            Previous
          </button>
          <span style={{ color: '#666' }}>Page {safePage + 1} of {totalPages}</span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={safePage >= totalPages - 1}
            style={{ padding: '6px 14px', border: '1px solid #ddd', borderRadius: 6, cursor: safePage >= totalPages - 1 ? 'default' : 'pointer', background: safePage >= totalPages - 1 ? '#f5f5f5' : '#fff' }}>
            Next
          </button>
        </div>
      )}

      {onboardOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 520, maxWidth: '92vw',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
            <h3 style={{ marginTop: 0 }}>{onboardTarget ? `Onboard ${onboardTarget.locationName || onboardTarget.locationId}` : 'Add a location PIT'}</h3>
            <p style={{ fontSize: 13, color: '#666' }}>
              The PIT needs the 4 read-only scopes: contacts, conversations,
              conversations/message, locations. It is validated live against GHL before saving.
            </p>
            <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Location ID</label>
            <input value={locIdInput} onChange={e => setLocIdInput(e.target.value)}
              disabled={!!onboardTarget} style={{ width: '100%', marginBottom: 12, fontFamily: 'monospace' }} />
            <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Private Integration Token</label>
            <input type="password" value={pitInput} onChange={e => setPitInput(e.target.value)}
              placeholder="pit-..." style={{ width: '100%', marginBottom: 12, fontFamily: 'monospace' }} />
            {onboardError && <div style={{ color: '#e53935', fontSize: 13, marginBottom: 8 }}>{onboardError}</div>}
            {onboardResult && (
              <div style={{ color: '#2e7d32', fontSize: 13, marginBottom: 8 }}>
                Validated and saved for {onboardResult.locationName}. {onboardResult.recoveredCount} queued call{onboardResult.recoveredCount === 1 ? '' : 's'} recovering.
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setOnboardOpen(false)}>{onboardResult ? 'Done' : 'Cancel'}</button>
              {!onboardResult && <button onClick={doOnboard} disabled={onboardBusy}>{onboardBusy ? 'Validating…' : 'Validate & Save'}</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
